import type { IssueMap, ReportData, ReportMeta, ReportStats } from "../types";
import type { AllBite } from "../AllBitesTable";
import type { BiteLink } from "../BitePanel";
import { AllBitesTable } from "../AllBitesTable";
import { KeyFindings } from "./KeyFindings";
import { PriorityFixes } from "./PriorityFixes";
import { ReportHeader } from "./ReportHeader";
import { CategorySummary } from "./CategorySummary";
import { SlideAttendance } from "./SlideAttendance";
import { FindingsByCategory } from "./FindingsByCategory";

export function ReportBody({
  report,
  stats,
  meta,
  issueMap,
  allBites,
  isLesson,
  onOpenBite,
}: {
  report: ReportData;
  stats: ReportStats;
  meta: ReportMeta;
  issueMap: IssueMap;
  allBites: AllBite[];
  isLesson: boolean;
  onOpenBite: (link: BiteLink) => void;
}) {
  const totalIssues = report.categories
    .flatMap((c) => c.subcategories)
    .flatMap((s) => s.issue_ids).length;

  const catStatsMap = new Map(
    report.categories.map((cat) => {
      const allIds = cat.subcategories.flatMap((s) => s.issue_ids);
      const critMaj = allIds.filter((id) => {
        const sev = issueMap[id]?.severity;
        return sev === "critical" || sev === "major";
      }).length;
      return [cat.name, { critMaj, total: allIds.length }];
    }),
  );

  return (
    <div className="space-y-8">

      {/* ── Header ── */}
      <ReportHeader
        meta={meta}
        issueMap={issueMap}
        allBites={allBites}
        totalIssues={totalIssues}
      />

      {/* ── Key Findings ── */}
      <KeyFindings findings={report.key_findings} />

      {/* ── Category Summary ── */}
      <CategorySummary categories={report.categories} issueMap={issueMap} catStatsMap={catStatsMap} />

      {/* ── Jump to All Bites ── */}
      {allBites.length > 0 && (
        <div className="-mt-4">
          <a href="#all-bites" className="text-sm text-primary hover:underline underline-offset-2">
            ↓ Jump to All Bites
          </a>
        </div>
      )}

      {/* ── Priority Fixes ── */}
      <PriorityFixes
        fixes={report.priority_fixes ?? []}
        subToCat={new Map(report.categories.flatMap(cat => cat.subcategories.map(sub => [sub.name, cat.name])))}
      />

      {/* ── Per-Slide Attendance ── */}
      <SlideAttendance slides={stats.slides} isLesson={isLesson} />

      {/* ── Findings by Category ── */}
      <FindingsByCategory
        categories={report.categories}
        issueMap={issueMap}
        catStatsMap={catStatsMap}
        isLesson={isLesson}
        onOpenBite={onOpenBite}
      />

      {/* ── All Bites ── */}
      {allBites.length > 0 && (
        <AllBitesTable
          allBites={allBites}
          onOpenBite={onOpenBite}
          isLesson={isLesson}
        />
      )}

    </div>
  );
}
